import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Location } from "../types";
import { useToast } from "../toast";
import LocationDialog from "../dialogs/LocationDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

export default function Locations({ onOpenDetail }: { onOpenDetail?: (id: number) => void }) {
  const api = useAuthApi();
  const [items, setItems] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Location | null>(null);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Location[]>(`${MGMT}/locations`);
      setItems(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/locations/${deleting.id}`, { method: "DELETE" });
      toast.show("Location deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((l) => {
      const haystack = [l.name, l.address, l.city, l.country, l.timezone].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
        >
          Add Location
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter locations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Latitude</TableCell>
              <TableCell>Longitude</TableCell>
              <TableCell>Timezone</TableCell>
              <TableCell align="right">VAT %</TableCell>
              <TableCell>Tax Jurisdiction</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters ? "No locations match the current filters." : "No locations yet."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((l) => (
              <TableRow
                key={l.id}
                hover
                sx={{ cursor: onOpenDetail ? "pointer" : undefined }}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("button")) return;
                  onOpenDetail?.(l.id);
                }}
              >
                <TableCell sx={{ fontWeight: 600 }}>{l.name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {l.address ?? "–"}
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {l.city ?? "–"}
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {l.country ?? "–"}
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                  {l.latitude != null ? l.latitude.toFixed(5) : "–"}
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                  {l.longitude != null ? l.longitude.toFixed(5) : "–"}
                </TableCell>
                <TableCell>{l.timezone}</TableCell>
                <TableCell align="right">{l.vat_rate_percent.toFixed(2)}</TableCell>
                <TableCell>{l.tax_jurisdiction || "–"}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(l)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(l)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <LocationDialog
        open={creating || editing !== null}
        location={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          load();
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        title="Delete location?"
        message={`Are you sure you want to delete "${deleting?.name}"?`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
